import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import nobgquickcaption from "../assets/quickcaption.PNG";


export default function Japanese() {
  const [darkMode, setDarkMode] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  // --- SEO + Dark Mode Handling ---
  useEffect(() => {
    // Dark mode
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }

    // SEO
  document.title = "QuickCaptionIO 日本語 - AIキャプション生成ツール";

const metaDescription = document.querySelector('meta[name="description"]');
if (metaDescription) {
  metaDescription.content =
    "QuickCaptionIOはAIで日本語キャプションと人気ハッシュタグを自動生成する無料ツールです。";
}


    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://quickcaption.io/japanese";
  }, []);

  const toggleDarkMode = () => {
    if (darkMode) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
    setDarkMode(!darkMode);
  };

  return (
    <div className="relative scroll-smooth min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-500">

      {/* ===================== HEADER ===================== */}
      <header className="sticky top-0 z-50 backdrop-blur bg-white/70 dark:bg-gray-900/70 border-b border-slate-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between h-16 items-center">
          <a href="#home" className="flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <Sparkles className="w-6 h-6 text-indigo-500" /> QuickCaptionIO
          </a>

          <nav className="hidden md:flex gap-6 font-semibold text-slate-700 dark:text-slate-300">
            <a href="#home" className="hover:text-indigo-500">ホーム</a>
            <a href="#how" className="hover:text-indigo-500">使い方</a>
            <a href="#about" className="hover:text-indigo-500">QuickCaptionとは？</a>
            <a href="https://apps.apple.com/us/app/quick-captionai/id6752661054" className="px-4 py-2 rounded-lg bg-indigo-500 text-white shadow hover:shadow-md">ダウンロード</a>
          </nav>

          <button
            onClick={toggleDarkMode}
            className="ml-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 transition"
          >
            {darkMode ? "🌞" : "🌙"}
          </button>
        </div>
      </header>

      {/* ===================== HERO ===================== */}
      <section id="home" className="relative flex flex-col-reverse md:flex-row items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <div className="w-full md:w-1/2 z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 text-slate-900 dark:text-white"
          >
            AIで簡単にキャプションとハッシュタグを作成
            <span className="text-indigo-500">、すぐに投稿可能。</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg sm:text-xl text-slate-700 dark:text-slate-300 mb-8 max-w-lg"
          >
            ランダムなキーワードを入力するだけで、AIがキャプションとトレンドハッシュタグを生成。
            <br />
            例: 「日本 初旅行 富士山」 → 「初めての日本、感動の瞬間 🇯🇵✨」 +
            <span className="text-indigo-500 font-semibold">#FujiViews #JapanTrip</span>
          </motion.p>
        </div>

        <div className="w-full md:w-1/2 flex justify-center mb-10 md:mb-0 relative">
          <motion.div className="absolute w-72 h-72 md:w-96 md:h-96 bg-gradient-to-tr from-indigo-400 via-pink-400 to-yellow-400 rounded-full filter blur-3xl opacity-40 animate-blob" />
          <motion.img
            src={nobgquickcaption}
            alt="QuickCaption Demo"
            className="relative w-60 md:w-80 z-20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />
        </div>
      </section>

      

      {/* ===================== HOW TO USE ===================== */}
     <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
  <motion.h2
    className="text-3xl sm:text-4xl font-bold mb-10 text-slate-900 dark:text-white"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
  >
    使い方
  </motion.h2>

  {/* Steps */}
  <div className="grid gap-8 md:grid-cols-3 mb-12">
    {[
      { step: "1", text: "キーワードを入力 (例: 海、夕日、旅行)" },
      { step: "2", text: "AIがキャプションと人気ハッシュタグを生成" },
      { step: "3", text: "コピーしてInstagram・TikTok・Xに投稿！" },
    ].map((item, idx) => (
      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: idx * 0.2 }}
        className="p-6 rounded-xl bg-white dark:bg-gray-800 shadow hover:shadow-lg transition"
      >
        <h3 className="text-2xl font-bold text-indigo-500 mb-3">{item.step}</h3>
        <p className="text-slate-700 dark:text-slate-300">{item.text}</p>
      </motion.div>
    ))}
  </div>

  {/* Images below steps with fade-in */}
  <div className="flex justify-center gap-6 flex-wrap">
    {[
      { src: require("../assets/quick-caption.PNG"), alt: "Quick Caption" },
      { src: require("../assets/quickcaptionai.PNG"), alt: "QuickCaption AI" },
      { src: require("../assets/quickcaptionio.PNG"), alt: "QuickCaptionIO" },
    ].map((img, idx) => (
      <motion.img
        key={idx}
        src={img.src}
        alt={img.alt}
        className="w-60 h-auto rounded-lg shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: idx * 0.3 }}
      />
    ))}
  </div>
</section>


      {/* ===================== ABOUT ===================== */}
      <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <motion.h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900 dark:text-white"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          QuickCaptionIOとは？
        </motion.h2>
        <p className="text-lg text-slate-700 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
          QuickCaptionIOは、SNS向けに最適化されたAIキャプション生成ツールです。
          旅行・グルメ・ファッションなど、どんなジャンルでもトレンドに合った文章を即座に作成。
          データ保存なしで安心して使え、初心者からインフルエンサーまで幅広くサポートします。
        </p>
      </section>

      {/* ===================== FAQ ===================== */}
      <section id="faq" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center text-slate-900 dark:text-white"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          よくある質問
        </motion.h2>
        <div className="space-y-4">
          {[
            { q: "無料で使えますか？", a: "はい、完全無料でご利用いただけます。ログインも不要です。" },
            { q: "どんなSNSに使えますか？", a: "Instagram、TikTok、X (Twitter)、Facebookなど主要なSNSすべてに対応しています。" },
            { q: "生成されたデータは保存されますか？", a: "いいえ。すべてのデータは保存されず、その場でのみ利用可能です。" },
          ].map((item, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex justify-between items-center p-4 font-medium text-left text-slate-900 dark:text-white"
              >
                {item.q}
                <ChevronDown className={`w-5 h-5 transform transition ${openIndex === idx ? "rotate-180" : ""}`} />
              </button>
              {openIndex === idx && (
                <div className="p-4 border-t border-slate-200 dark:border-gray-700 text-slate-700 dark:text-slate-300">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ===================== DOWNLOAD CTA ===================== */}
      <section id="download" className="bg-indigo-50 dark:bg-gray-800 py-24 text-center">
        <motion.h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900 dark:text-white"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          アプリをダウンロード
        </motion.h2>
        <p className="text-slate-700 dark:text-slate-300 mb-6">
          すぐに始めて、キャプション作成をもっと簡単に。
        </p>
        <div className="flex justify-center gap-6 flex-wrap">
          <a href="#" className="px-6 py-3 rounded-xl bg-black text-white font-semibold shadow-lg hover:scale-105 transition">
            App Store
          </a>
        </div>
      </section>
{/* ===================== FOUNDER ===================== */}
<section
  id="founder"
  className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 my-12 text-center bg-white dark:bg-gray-900 rounded-2xl shadow-xl relative overflow-hidden"
>
  {/* Background Gradient Blob */}
  <motion.div
    className="absolute top-0 left-1/2 w-96 h-96 bg-gradient-to-tr from-indigo-400 via-pink-400 to-yellow-400 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 animate-blob"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1.5 }}
  />

  <motion.h2
    className="text-3xl sm:text-4xl font-extrabold mb-6 text-slate-900 dark:text-white relative z-10"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
  >
    Meet the Founder
  </motion.h2>

  {/* Profile Image */}
  <motion.div
    className="flex justify-center mb-6 relative z-10"
    initial={{ opacity: 0, scale: 0.8 }}
    whileInView={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.8, delay: 0.2 }}
  >
    <img
      src={require("../assets/bikashyu.png")}
      alt="Bikashyu"
      className="w-32 h-32 rounded-full border-4 border-indigo-500 shadow-lg"
    />
  </motion.div>

  <motion.p
    className="text-lg text-slate-700 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed relative z-10"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: 0.4 }}
  >
    こんにちは、私はBikashyuです 👋 — 旅行、動画制作、アプリ開発が大好きなウェブ開発者です。  
    QuickCaptionIOでは、ランダムな単語やハッシュタグを入力するだけで、  
    AIが瞬時に投稿可能なキャプションとトレンドハッシュタグに変換します。  
    私の目標は、あなたの時間を節約し、コンテンツ作成を楽しく簡単にすることです。
  </motion.p>
</section>

      {/* ===================== FOOTER ===================== */}
      <footer className="bg-gradient-to-t from-indigo-50 dark:from-gray-900/50 to-white dark:to-gray-900/80 border-t border-slate-200/50 dark:border-gray-700/50 py-12 text-center">
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          &copy; {new Date().getFullYear()} QuickCaptionIO. All rights reserved.
        </p>
        <Link to="/" className="text-indigo-500 font-semibold hover:underline">English</Link>
      </footer>

    </div>
  );
}
